"use client"

import { useEffect, useState } from "react"
import { HorizontalCarousel } from "./horizontal-carousel"
import { ProductCarouselCard, type CarouselProduct } from "./product-carousel-card"
import {
  getTrendingInCollection,
  getProductImage,
} from "@/lib/recommendations"
import { formatPrice } from "@/lib/format-utils"
import type { ToolCarouselProduct } from "@/lib/tool-carousel-key"
import { usePrefetchedToolCarousel } from "./tool-carousel-prefetch"

interface ToolProductCarouselProps {
  /** Optional eyebrow above the title */
  eyebrow?: string
  /** Section title */
  title: string
  /** Optional sub-copy */
  description?: string
  /** Supabase product `type` to fetch — e.g. "french-door", "side-by-side", "top-freezer" */
  collectionType: string
  /** Optional fallback collection if the primary has no matches */
  fallbackCollectionType?: string
  /** Max number of products to pull (default 8) */
  limit?: number
  /**
   * When true, shuffles the result pool so two carousels with the same
   * collectionType render different products (or at least different order).
   * Used by the bonus rails in ToolPageShell to avoid duplicating the
   * variant body's own carousels on tools that share a collection.
   */
  randomize?: boolean
  /**
   * OR-of-ilike title terms forwarded to getTrendingInCollection, for
   * sub-families the type column cannot express (nugget vs cube ice
   * makers — FRIDGE-578 follow-up). Code constants only.
   */
  titleMatch?: string[]
  /** Additional classes on the outer carousel */
  className?: string
}

/**
 * ToolProductCarousel — full-bleed horizontal carousel of real products
 * pulled from Supabase via `getTrendingInCollection`. Maps our
 * `RecommendedProduct` shape onto the brand-kit `ProductCarouselCard`
 * so every tool-page variant renders the same product treatment as the
 * homepage and collection pages.
 *
 * Fetches client-side on mount. Renders nothing until products arrive
 * (or if the collection has zero matches), to avoid a flash of empty
 * carousel on pages that use this inline.
 */
export function ToolProductCarousel({
  eyebrow,
  title,
  description,
  collectionType,
  fallbackCollectionType,
  limit = 8,
  randomize = false,
  titleMatch,
  className = "",
}: ToolProductCarouselProps) {
  // FRIDGE-1501 (F2): a server page may have read this carousel already
  // (ToolCarouselPrefetch); then its cards are in the server HTML and the
  // browser does not fetch them again. A browser read is a whole row; the
  // card reads the ToolCarouselProduct fields of either.
  const prefetched = usePrefetchedToolCarousel({ collectionType, fallbackCollectionType, limit, randomize, titleMatch })
  const [products, setProducts] = useState<ToolCarouselProduct[]>(prefetched ?? [])

  useEffect(() => {
    if (prefetched) return
    let cancelled = false
    async function fetchProducts() {
      try {
        let rows = await getTrendingInCollection(collectionType, limit, { randomize, titleMatch })
        if ((!rows || rows.length === 0) && fallbackCollectionType) {
          rows = await getTrendingInCollection(fallbackCollectionType, limit, { randomize, titleMatch })
        }
        if (!cancelled) setProducts(rows ?? [])
      } catch {
        // Silently swallow — product rail is optional, never blocks page.
      }
    }
    fetchProducts()
    return () => {
      cancelled = true
    }
    // titleMatch is a code-constant array; join is a stable dep key.
  }, [collectionType, fallbackCollectionType, limit, randomize, titleMatch?.join(','), prefetched])

  if (products.length === 0) return null

  const cards: CarouselProduct[] = products.map((p) => ({
    brand: p.brand,
    title: p.title,
    // `price`/`originalPrice` on RecommendedProduct are already in dollars
    // (lib/recommendations.ts divides by 100 before returning)
    price:
      typeof p.price === "number" && p.price > 0
        ? formatPrice(p.price)
        : "",
    originalPrice:
      typeof p.originalPrice === "number" && p.originalPrice > 0
        ? formatPrice(p.originalPrice)
        : undefined,
    // FRIDGE-1532: the badge states a fact the row holds; the old POPULAR fallback had no popularity data behind it.
    badge: p.specs_json?.energy_star ? "ENERGY STAR" : undefined,
    badgeColor: p.specs_json?.energy_star ? "bg-[#0469f0]" : undefined,
    image: getProductImage(p.images),
    href: p.canonical_url ? p.canonical_url : `/products/${p.id}`,
    rating: p.rating,
    reviews: p.reviews,
  }))

  return (
    <div className={`max-w-[1320px] mx-auto px-6 md:px-8 ${className}`}>
      <HorizontalCarousel
        eyebrow={eyebrow}
        title={title}
        description={description}
        bgClass="bg-gray-50"
        className="!mt-0"
      >
        {cards.map((p) => (
          <ProductCarouselCard key={`${p.brand}-${p.title}`} product={p} />
        ))}
      </HorizontalCarousel>
    </div>
  )
}
